import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Modal, Group, Select, TextInput, Button, Grid, GridCol, Textarea, Accordion } from '@mantine/core';
import { useForm } from '@mantine/form';
import { DateTimePicker } from '@mantine/dates';
import ModalComponent from '../../../components/modal';
import { Language } from '../../../languages/languageHandler';
import { ticketStatus, ticketImpact, ticketUrgency, ticketClassification, ticketSolution } from '../../utils';
import { HeldeskTickeLineRequests, addTicketLine, updateTicketLine } from '../../../../libraries/helpdesk/tickets';
import { useSelector } from 'react-redux';
import { IUser } from '../../../../libraries/user';
import { AppState } from '../../../client-redux/reducers';
import { formatDateToISO, getCurrentDateTimeISO } from '../../../utils/date';
import classes from '../../../components/util/date-time-picker.module.scss';
import { StatusType } from '../../../../libraries/zps';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../../context/status-oper-dialog';
import './styles.scss';

interface ModalProps {
  isOpen: boolean;
  hideBtn: () => void;
  ticketData: any;
  mode?: string;
  onReload: () => void;
}

const TicketEditModal = ({ isOpen, hideBtn, ticketData, mode, onReload }: ModalProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const loginInfo = useSelector<AppState, IUser>(state => state.login.user);

  const { ticketID } = useParams<{ ticketID: string }>();
  const [, setTicket] = useState(ticketData);
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const location = useLocation();

  const { showStatusOperDialog, handleApiError } = useStatusOperDialog();

  useEffect(() => {
    const path = location.pathname;
    const editable = /\/helpdesk\/ticket\/(edit|new)/.test(path);
    setIsEditable(editable);
  }, [location]);

  const form = useForm({
    initialValues: {
      classification: 0,
      comments: '',
      description: '',
      impact: 0,
      line: 0,
      origin: '',
      soluClassif: false,
      status: 0,
      statusDT: getCurrentDateTimeISO(),
      ticketID: 0,
      time: 1,
      urgency: 0,
      user: loginInfo
    }
  });

  useEffect(() => {
    // If we have data (either for update or for prefill on add)
    if (ticketData) {
      setTicket(ticketData);
      form.setValues({
        ...ticketData,
        status: ticketData.status?.toString() ?? '0',
        urgency: ticketData.urgency?.toString() ?? '0',
        impact: ticketData.impact?.toString() ?? '0',
        classification: ticketData.classification?.toString() ?? '0',
        soluClassif: ticketData.soluClassif === true ? 'true' : 'false',
        statusDT: ticketData.statusDT ? new Date(ticketData.statusDT).toISOString() : getCurrentDateTimeISO(),
        comments: ticketData.comments || '',
        description: ticketData.description || '',
        ticketID: Number(ticketID),
        user: loginInfo
      });
    } else {
      // fallback (if no previous line exists)
      form.setValues({
        classification: 0,
        comments: '',
        description: '',
        impact: 0,
        line: 0,
        origin: '',
        soluClassif: false,
        status: 0,
        statusDT: getCurrentDateTimeISO(),
        ticketID: Number(ticketID),
        time: 1,
        urgency: 0,
        user: loginInfo
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketData, mode]);

  // Submit form logic
  const submitForm = async (ticketInfo: any) => {
    const formController = new AbortController();

    const request: HeldeskTickeLineRequests = {
      classification: ticketInfo.classification,
      comments: ticketInfo.comments,
      description: ticketInfo.description,
      impact: ticketInfo.impact,
      line: ticketInfo.line?.toString(),
      origin: ticketInfo.origin,
      soluClassif: ticketInfo.soluClassif,
      status: ticketInfo.status,
      statusDT: formatDateToISO(ticketInfo.statusDT) || formatDateToISO(new Date(ticketInfo.statusDT)),
      ticketID: ticketInfo.ticketID || Number(ticketID),
      time: ticketInfo.time,
      urgency: ticketInfo.urgency,
      user: ticketInfo.user
    };

    try {
      const response =
        mode === 'add' ? await addTicketLine(request, formController) : await updateTicketLine(request, formController);
      if (isZarphSuccessResponse(response)) {
        showStatusOperDialog({
          type: StatusType.INFO,
          message:
            mode === 'update'
              ? lang.HELPDESK_EDIT_TICKET_ALERT_UPDATED_SUCCESS || 'Ticket edited successfully'
              : lang.HELPDESK_ADD_LINE_ALERT_UPDATED_SUCCESS || 'Line added successfully',
          statusOper: response.data?.statusOper
        });

        setTimeout(() => {
          hideBtn();
          onReload();
        }, 800);

        return;
      }
      handleApiError(
        response,
        mode === 'update' ? lang.HELPDESK_EDIT_TICKET_ALERT_UPDATED_ERROR : lang.HELPDESK_ADD_LINE_ALERT_UPDATED_ERROR
      );
    } catch (error) {
      handleApiError(
        error,
        mode === 'update'
          ? lang.HELPDESK_EDIT_TICKET_ALERT_UPDATED_ERROR || 'Error updating ticket'
          : lang.HELPDESK_ADD_LINE_ALERT_UPDATED_ERROR || 'Error adding line'
      );
    }
  };

  return (
    <>
      <ModalComponent
        show={isOpen}
        handleClick={hideBtn}
        title={
          mode === 'add'
            ? lang.HELPDESK_NEW_TICKET_MODAL_TITLE || 'New Status'
            : lang.HELPDESK_EDIT_TICKET_MODAL_TITLE || 'Edit Status'
        }
        content={
          <Modal.Body>
            {/* Form initialization */}
            <form
              onSubmit={form.onSubmit(values => {
                submitForm(values);
              })}
            >
              <Group classNames={{ root: 'ticket-edit' }}>
                <Grid>
                  {/* Status */}
                  <GridCol span={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                    <Select
                      label={lang.EQUIPMENT_COM_STATE}
                      data={ticketStatus(lang)}
                      disabled={!isEditable}
                      {...form.getInputProps('status')}
                    />
                  </GridCol>
                  {/* Open date */}
                  <GridCol span={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                    <DateTimePicker
                      classNames={{
                        day: classes.day
                      }}
                      label={lang.GLOBAL_DATE}
                      value={form.values.statusDT ? new Date(form.values.statusDT) : null}
                      onChange={date => form.setFieldValue('statusDT', date ? date.toString() : '')}
                      disabled={!isEditable}
                      valueFormat="YYYY/MM/DD HH:mm"
                      timePickerProps={{
                        withDropdown: true,
                        popoverProps: { withinPortal: false },
                        format: '24h',
                        classNames: {
                          input: classes.timeInput,
                          field: classes.timeField,
                          control: classes.timeControl
                        }
                      }}
                    />
                  </GridCol>

                  {/* Origin */}
                  <GridCol span={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                    <TextInput label={lang.GLOBAL_ORIGIN} disabled={!isEditable} {...form.getInputProps('origin')} />
                  </GridCol>

                  {/* Solution */}
                  <GridCol span={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                    <Select
                      label={lang.GLOBAL_SOLUTION}
                      data={ticketSolution(lang)}
                      disabled={!isEditable}
                      {...form.getInputProps('soluClassif')}
                    />
                  </GridCol>

                  {/* Time */}
                  <GridCol span={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                    <TextInput label={lang.GLOBAL_TIME} disabled={!isEditable} {...form.getInputProps('time')} />
                  </GridCol>

                  {/* Impact */}
                  <GridCol span={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                    <Select
                      label={lang.GLOBAL_IMPACT}
                      data={ticketImpact(lang)}
                      disabled={!isEditable}
                      {...form.getInputProps('impact')}
                    />
                  </GridCol>

                  {/* Urgency */}
                  <GridCol span={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                    <Select
                      label={lang.GLOBAL_URGENCY}
                      data={ticketUrgency(lang)}
                      disabled={!isEditable}
                      {...form.getInputProps('urgency')}
                    />
                  </GridCol>

                  {/* Classification */}
                  <GridCol span={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                    <Select
                      label={lang.GLOBAL_CLASSIFICATION}
                      data={ticketClassification(lang)}
                      disabled={!isEditable}
                      {...form.getInputProps('classification')}
                    />
                  </GridCol>
                </Grid>
              </Group>

              <Group className="description-comment">
                {/* Description */}
                <Textarea
                  label={lang.EQUIPMENT_DESCRIPTION}
                  disabled={!isEditable}
                  {...form.getInputProps('description')}
                />

                {/* Comments inside Accordion */}
                <Accordion variant="unstyled">
                  <Accordion.Item value="comments">
                    <Accordion.Control>{lang.GLOBAL_COMMENTS}</Accordion.Control>
                    <Accordion.Panel>
                      <Textarea label={null} {...form.getInputProps('comments')} disabled={!isEditable} />
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              </Group>

              <Group className="save-button">
                {isEditable && (
                  <Button
                    color="#538539"
                    className="save-button__btn"
                    radius="md"
                    onClick={e => {
                      e.preventDefault();
                      submitForm(form.values);
                    }}
                  >
                    {lang.GLOBAL_SAVE_BUTTON}
                  </Button>
                )}
              </Group>
            </form>
          </Modal.Body>
        }
      />
    </>
  );
};

export default TicketEditModal;
